import React, { useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Collapse,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	InputAdornment,
	MenuItem,
	Paper,
	Select,
	TextField,
	Typography,
} from '@mui/material';
import { calcularPrecioFinal } from 'features/salesOrder/components/SalesOrderServiceForm/helpers';
import { FlightAncillaryTypeEnum, TransferTypeEnum, TypeServiceEnum, typeTransfer } from 'features/salesOrder/types';
import { LoadedQuotation, WizardSavedPassenger } from '../../types';
import {
	ExtraServiceItem,
	buildFeeServiceDraft,
	buildFlightAncillaryServiceDraft,
	buildOtherServiceDraft,
	buildTransferServiceDraft,
	collectExtraServices,
} from '../../mappers/quotationServiceMappers';
import {
	ExtraServiceFormDraft,
	WIZARD_FLIGHT_ANCILLARY_TYPE_OPTIONS,
	WIZARD_OTHER_SERVICE_TYPES,
	WIZARD_SALES_ORDER_SERVICE_TYPE_OPTIONS,
	computeQuotationServiceTotalCost,
	createExtraServiceFormDraft,
	normalizeReferenceForApi,
	serviceTypeShowsLimitDateOnly,
	serviceTypeShowsTravelDates,
	serviceTypeUsesOtherSubtype,
} from '../../helpers/extraServiceDraft';
import { saveServiceToOrder } from '../../helpers/saveService';
import { WizardProviderAutocomplete } from '../WizardProviderAutocomplete';

interface Props {
	quotation: LoadedQuotation;
	saleOrderId: string;
	savedPassengers: WizardSavedPassenger[];
	onComplete: () => void;
}

const DATE_DISCLAIMER =
	'Las fechas se precargaron con la fecha de inicio del viaje (o la del destino en la cotización). Revisá cada servicio y corregí las fechas según lo cotizado antes de guardar.';

export const StepExtraServices = ({ quotation, saleOrderId, savedPassengers, onComplete }: Props): JSX.Element => {
	const items = useMemo(() => collectExtraServices(quotation), [quotation]);
	const markup = quotation.markup ?? 0;

	const [drafts, setDrafts] = useState<ExtraServiceFormDraft[]>(() =>
		items.map((item) => createExtraServiceFormDraft(item, quotation, savedPassengers)),
	);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [progress, setProgress] = useState('');
	const [datesVerified, setDatesVerified] = useState(false);

	const updateDraft = (index: number, patch: Partial<ExtraServiceFormDraft>) => {
		setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
	};

	const updateSegment = (index: number, patch: Partial<ExtraServiceFormDraft['segment']>) => {
		setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, segment: { ...d.segment, ...patch } } : d)));
	};

	const needsPassengerSelection = (draft: ExtraServiceFormDraft, item: ExtraServiceItem): boolean => {
		if (draft.salesOrderType === TypeServiceEnum.Transfer) {
			return true;
		}
		if (draft.salesOrderType === TypeServiceEnum.FlightAncillary) {
			return true;
		}
		if (draft.salesOrderType === TypeServiceEnum.Fee) {
			return item.service.serviceType === 'perPassenger';
		}
		return item.service.serviceType === 'perPassenger';
	};

	if (items.length === 0) {
		return (
			<Box>
				<Alert severity="info" sx={{ mb: 2 }}>
					No hay servicios adicionales en la cotización.
				</Alert>
				<Box display="flex" justifyContent="flex-end">
					<Button variant="contained" onClick={onComplete}>
						Continuar al resumen
					</Button>
				</Box>
			</Box>
		);
	}

	const handleSaveSelected = async () => {
		const toSave = items.map((item, index) => ({ item, index, draft: drafts[index] })).filter((x) => x.draft.enabled);

		if (toSave.length === 0) {
			onComplete();
			return;
		}

		if (!datesVerified) {
			setError('Confirmá que revisaste las fechas de los servicios antes de continuar.');
			return;
		}

		for (const { draft, index, item } of toSave) {
			if (!draft.tariffProviderId?.trim()) {
				setError(`Seleccioná el proveedor del servicio ${index + 1}`);
				return;
			}
			if (draft.cost <= 0) {
				setError(`Indicá un costo válido en el servicio ${index + 1}`);
				return;
			}
			if (
				draft.salesOrderType === TypeServiceEnum.Transfer &&
				(!draft.segment.originName.trim() || !draft.segment.destinationName.trim())
			) {
				setError(`Completá origen y destino del traslado ${index + 1}`);
				return;
			}
			if (needsPassengerSelection(draft, item) && draft.passengerIds.length === 0) {
				setError(`Asigná pasajeros al servicio ${index + 1}`);
				return;
			}
			if (draft.salesOrderType === TypeServiceEnum.FlightAncillary && !draft.validatingCarrierCode.trim()) {
				setError(`Indicá el validating carrier (código IATA) en el servicio ${index + 1}`);
				return;
			}
		}

		setLoading(true);
		setError(null);

		try {
			for (let i = 0; i < toSave.length; i += 1) {
				const { item, draft } = toSave[i];
				setProgress(`Guardando ${i + 1} de ${toSave.length}: ${item.service.name}`);

				const reference = normalizeReferenceForApi(draft.reference);
				const isPerGroup = item.service.serviceType === 'perGroup';
				const common = {
					reference,
					cost: draft.cost,
					tariffProviderId: draft.tariffProviderId,
					serviceProviderId: draft.serviceProviderId,
					sameTariffServiceProvider: draft.sameTariffServiceProvider,
					passengerIds: draft.passengerIds,
				};

				if (draft.salesOrderType === TypeServiceEnum.Transfer) {
					const payload = buildTransferServiceDraft({
						quotation,
						...common,
						segment: draft.segment,
					});
					await saveServiceToOrder(saleOrderId, payload as unknown as Record<string, unknown>);
				} else if (draft.salesOrderType === TypeServiceEnum.FlightAncillary) {
					const payload = buildFlightAncillaryServiceDraft({
						quotation,
						serviceName: item.service.name,
						reference,
						cost: draft.cost,
						tariffProviderId: draft.tariffProviderId,
						passengerIds: draft.passengerIds,
						limitDate: draft.limitDate,
						flightAncillaryType: draft.flightAncillaryType,
						validatingCarrierCode: draft.validatingCarrierCode,
						isPerGroup,
					});
					await saveServiceToOrder(saleOrderId, payload as unknown as Record<string, unknown>);
				} else if (draft.salesOrderType === TypeServiceEnum.Fee) {
					const payload = buildFeeServiceDraft({
						quotation,
						cost: draft.cost,
						tariffProviderId: draft.tariffProviderId,
						passengerIds: draft.passengerIds,
						isPerGroup,
						feeServiceType: draft.feeServiceType,
					});
					await saveServiceToOrder(saleOrderId, payload as unknown as Record<string, unknown>);
				} else {
					const payload = buildOtherServiceDraft({
						service: item.service,
						quotation,
						startDate: draft.startDate,
						endDate: draft.endDate,
						limitDate: draft.limitDate,
						otherServiceType: draft.otherServiceType,
						...common,
					});
					await saveServiceToOrder(saleOrderId, payload as unknown as Record<string, unknown>);
				}
			}
			onComplete();
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Error al guardar servicios');
		} finally {
			setLoading(false);
			setProgress('');
		}
	};

	return (
		<Box>
			<Typography variant="h6" fontWeight={700} gutterBottom>
				Otros servicios
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				Revisá tipo de servicio en la orden, proveedor y costos. La referencia es opcional si aún no tenés localizador.
			</Typography>

			<Alert severity="warning" sx={{ mb: 2 }}>
				{DATE_DISCLAIMER}
			</Alert>

			{items.map((item, index) => {
				const draft = drafts[index];
				if (!draft) {
					return null;
				}
				const saleAmount = draft.cost > 0 ? parseFloat(calcularPrecioFinal(draft.cost, markup)) : 0;
				const isPerGroup = item.service.serviceType === 'perGroup';
				const isRoundTrip =
					draft.segment.transferType === TransferTypeEnum.InOut ||
					draft.segment.transferType === TransferTypeEnum.InOutPrivate;
				const showPassengers = needsPassengerSelection(draft, item) || !isPerGroup;

				return (
					<Paper key={`${item.kind}-${index}-${item.service.name}`} variant="outlined" sx={{ p: 2, mb: 2 }}>
						<FormControlLabel
							control={
								<Checkbox checked={draft.enabled} onChange={(e) => updateDraft(index, { enabled: e.target.checked })} />
							}
							label={
								<Box>
									<Typography fontWeight={600}>{item.service.name}</Typography>
									<Typography variant="caption" color="text.secondary">
										Cotización: ${item.service.fareCost} · {item.service.serviceType}
										{item.kind === 'manual' ? ' · manual' : ''}
									</Typography>
								</Box>
							}
						/>

						<Collapse in={draft.enabled}>
							<Divider sx={{ my: 2 }} />
							<Grid container spacing={2}>
								<Grid item xs={12} sm={6}>
									<Typography variant="caption" color="text.secondary" display="block" gutterBottom>
										Tipo en la orden de venta
									</Typography>
									<FormControl fullWidth size="small">
										<Select
											value={draft.salesOrderType}
											onChange={(e) => {
												const salesOrderType = e.target.value as TypeServiceEnum;
												const patch: Partial<ExtraServiceFormDraft> = { salesOrderType };
												if (
													(salesOrderType === TypeServiceEnum.Transfer ||
														salesOrderType === TypeServiceEnum.FlightAncillary) &&
													draft.passengerIds.length === 0
												) {
													patch.passengerIds = savedPassengers.map((p) => p.keyId);
												}
												updateDraft(index, patch);
											}}
										>
											{WIZARD_SALES_ORDER_SERVICE_TYPE_OPTIONS.map((opt) => (
												<MenuItem key={opt.value} value={opt.value}>
													{opt.label}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>

								{serviceTypeUsesOtherSubtype(draft.salesOrderType) && (
									<Grid item xs={12} sm={6}>
										<Typography variant="caption" color="text.secondary" display="block" gutterBottom>
											Subtipo (Otros)
										</Typography>
										<FormControl fullWidth size="small">
											<Select
												value={draft.otherServiceType}
												onChange={(e) => updateDraft(index, { otherServiceType: e.target.value })}
											>
												{WIZARD_OTHER_SERVICE_TYPES.map((opt) => (
													<MenuItem key={opt.value} value={opt.value}>
														{opt.name}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Grid>
								)}

								{draft.salesOrderType === TypeServiceEnum.FlightAncillary && (
									<>
										<Grid item xs={12} sm={6}>
											<Typography variant="caption" color="text.secondary" display="block" gutterBottom>
												Tipo de adicional
											</Typography>
											<FormControl fullWidth size="small">
												<Select
													value={draft.flightAncillaryType}
													onChange={(e) =>
														updateDraft(index, {
															flightAncillaryType: e.target.value as FlightAncillaryTypeEnum,
														})
													}
												>
													{WIZARD_FLIGHT_ANCILLARY_TYPE_OPTIONS.map((opt) => (
														<MenuItem key={opt.value} value={opt.value}>
															{opt.label}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										</Grid>
										<Grid item xs={12} sm={3}>
											<TextField
												fullWidth
												size="small"
												label="Validating carrier"
												required
												value={draft.validatingCarrierCode}
												onChange={(e) =>
													updateDraft(index, {
														validatingCarrierCode: e.target.value.toUpperCase().slice(0, 3),
													})
												}
												inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
												helperText="Código IATA (ej. de la opción de vuelo cotizada)"
											/>
										</Grid>
									</>
								)}

								<Grid item xs={12} sm={6}>
									<WizardProviderAutocomplete
										value={draft.tariffProviderId}
										onChange={(id) =>
											updateDraft(index, {
												tariffProviderId: id,
												serviceProviderId: draft.sameTariffServiceProvider ? id : draft.serviceProviderId,
											})
										}
										error={draft.enabled && !draft.tariffProviderId}
									/>
								</Grid>

								<Grid item xs={12} sm={6}>
									<FormControlLabel
										control={
											<Checkbox
												checked={draft.sameTariffServiceProvider}
												onChange={(e) =>
													updateDraft(index, {
														sameTariffServiceProvider: e.target.checked,
														serviceProviderId: e.target.checked ? draft.tariffProviderId : draft.serviceProviderId,
													})
												}
											/>
										}
										label="Mismo proveedor de tarifa y servicio"
									/>
								</Grid>

								{!draft.sameTariffServiceProvider &&
									draft.salesOrderType !== TypeServiceEnum.FlightAncillary &&
									draft.salesOrderType !== TypeServiceEnum.Fee && (
										<Grid item xs={12} sm={6}>
											<WizardProviderAutocomplete
												value={draft.serviceProviderId}
												onChange={(id) => updateDraft(index, { serviceProviderId: id })}
												label="Prestador del servicio"
											/>
										</Grid>
									)}

								{draft.salesOrderType !== TypeServiceEnum.Fee && (
									<Grid item xs={12} sm={4}>
										<TextField
											fullWidth
											size="small"
											label="Referencia (opcional)"
											placeholder="Localizador si lo tenés"
											value={draft.reference}
											onChange={(e) => updateDraft(index, { reference: e.target.value.toUpperCase() })}
											inputProps={{ style: { textTransform: 'uppercase' } }}
											helperText="Podés dejarlo vacío y completarlo después en la orden"
										/>
									</Grid>
								)}

								<Grid item xs={12} sm={4}>
									<TextField
										fullWidth
										size="small"
										type="number"
										label="Costo total (USD)"
										inputProps={{ min: 0, step: 0.01 }}
										value={draft.cost}
										onChange={(e) => {
											const parsed = parseFloat(e.target.value.replace(',', '.'));
											updateDraft(index, { cost: Number.isFinite(parsed) ? parsed : 0 });
										}}
										InputProps={{
											startAdornment: <InputAdornment position="start">$</InputAdornment>,
										}}
										helperText={
											isPerGroup
												? 'Cotizado por grupo'
												: `Sugerido: $${computeQuotationServiceTotalCost(item.service, savedPassengers.length)} (${
														savedPassengers.length
												  } pax)`
										}
									/>
								</Grid>

								<Grid item xs={12} sm={4}>
									<TextField
										fullWidth
										size="small"
										label="Venta (USD)"
										value={saleAmount.toFixed(2)}
										disabled
										helperText={`Markup cotización: ${markup}%`}
										InputProps={{
											startAdornment: <InputAdornment position="start">$</InputAdornment>,
										}}
									/>
								</Grid>

								{serviceTypeShowsTravelDates(draft.salesOrderType) && (
									<>
										<Grid item xs={12} sm={4}>
											<TextField
												fullWidth
												size="small"
												type="date"
												label="Fecha inicio"
												value={draft.startDate}
												onChange={(e) => updateDraft(index, { startDate: e.target.value })}
												InputLabelProps={{ shrink: true }}
											/>
										</Grid>
										<Grid item xs={12} sm={4}>
											<TextField
												fullWidth
												size="small"
												type="date"
												label="Fecha fin"
												value={draft.endDate}
												onChange={(e) => updateDraft(index, { endDate: e.target.value })}
												InputLabelProps={{ shrink: true }}
											/>
										</Grid>
										<Grid item xs={12} sm={4}>
											<TextField
												fullWidth
												size="small"
												type="date"
												label="Fecha límite"
												value={draft.limitDate}
												onChange={(e) => updateDraft(index, { limitDate: e.target.value })}
												InputLabelProps={{ shrink: true }}
											/>
										</Grid>
									</>
								)}

								{serviceTypeShowsLimitDateOnly(draft.salesOrderType) && (
									<Grid item xs={12} sm={4}>
										<TextField
											fullWidth
											size="small"
											type="date"
											label="Fecha límite"
											value={draft.limitDate}
											onChange={(e) => updateDraft(index, { limitDate: e.target.value })}
											InputLabelProps={{ shrink: true }}
										/>
									</Grid>
								)}

								{showPassengers && (
									<Grid item xs={12}>
										<Typography variant="caption" color="text.secondary" display="block" gutterBottom>
											Pasajeros
										</Typography>
										<FormControl fullWidth size="small">
											<Select
												multiple
												value={draft.passengerIds}
												onChange={(e) =>
													updateDraft(index, {
														passengerIds:
															typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
													})
												}
												renderValue={(selected) =>
													(selected as string[])
														.map((id) => {
															const p = savedPassengers.find((sp) => sp.keyId === id);
															return p ? `${p.name} ${p.lastName}`.trim() : id;
														})
														.join(', ')
												}
											>
												{savedPassengers.map((p) => (
													<MenuItem key={p.keyId} value={p.keyId}>
														{p.name} {p.lastName}
														{p.slotLabel ? ` · ${p.slotLabel}` : ''}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Grid>
								)}

								{draft.salesOrderType === TypeServiceEnum.Transfer && (
									<>
										<Grid item xs={12}>
											<Typography variant="subtitle2" fontWeight={600}>
												Tramo del traslado
											</Typography>
										</Grid>
										<Grid item xs={12} sm={4}>
											<FormControl fullWidth size="small">
												<Select
													value={draft.segment.transferType}
													onChange={(e) =>
														updateSegment(index, {
															transferType: e.target.value as TransferTypeEnum,
														})
													}
												>
													{typeTransfer.map((t) => (
														<MenuItem key={t.id} value={t.id}>
															{t.name}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										</Grid>
										<Grid item xs={12} sm={4}>
											<TextField
												fullWidth
												size="small"
												label="Origen"
												required
												value={draft.segment.originName}
												onChange={(e) => updateSegment(index, { originName: e.target.value })}
											/>
										</Grid>
										<Grid item xs={12} sm={4}>
											<TextField
												fullWidth
												size="small"
												label="Destino"
												required
												value={draft.segment.destinationName}
												onChange={(e) => updateSegment(index, { destinationName: e.target.value })}
											/>
										</Grid>
										<Grid item xs={12} sm={4}>
											<TextField
												fullWidth
												size="small"
												type="date"
												label="Fecha del traslado"
												value={draft.segment.startDate}
												onChange={(e) => updateSegment(index, { startDate: e.target.value })}
												InputLabelProps={{ shrink: true }}
											/>
										</Grid>
										{isRoundTrip && (
											<Grid item xs={12} sm={4}>
												<TextField
													fullWidth
													size="small"
													type="date"
													label="Fecha de regreso"
													value={draft.segment.endDate}
													onChange={(e) => updateSegment(index, { endDate: e.target.value })}
													InputLabelProps={{ shrink: true }}
												/>
											</Grid>
										)}
									</>
								)}
							</Grid>
						</Collapse>
					</Paper>
				);
			})}

			{progress && (
				<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
					{progress}
				</Typography>
			)}

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			<FormControlLabel
				sx={{ mb: 2, display: 'block' }}
				control={<Checkbox checked={datesVerified} onChange={(e) => setDatesVerified(e.target.checked)} />}
				label="Verifiqué que las fechas de los servicios a importar son correctas según lo cotizado"
			/>

			<Box display="flex" justifyContent="space-between">
				<Button variant="text" onClick={onComplete} disabled={loading}>
					Omitir todos
				</Button>
				<Button variant="contained" color="success" onClick={() => void handleSaveSelected()} disabled={loading}>
					{loading ? <CircularProgress size={22} color="inherit" /> : 'Guardar seleccionados y continuar'}
				</Button>
			</Box>
		</Box>
	);
};
